/**
 * ENG-7748 - Anchor Browser persistent-session lifecycle (host/manager side).
 *
 * Owns the per-agent minted Anchor session: mint a profile-bound session before
 * the agent's MCP connection needs it, reuse it while valid, and re-mint
 * PROACTIVELY before it expires. The returned session id is stamped into the
 * anchor-browser integration's `config.session_id` by the manager, which
 * `writeIntegrations` materializes as `ANCHOR_BROWSER_SESSION_ID`. Because
 * anchor-browser routes through the stdio remote-MCP proxy (liveHeaderRefresh),
 * the proxy reads that header LIVE per request, so a re-mint takes effect with no
 * agent respawn.
 *
 * Fail-soft by contract: no profile configured, no api key, or a mint error all
 * resolve to "no session id" (the header stays empty → stateless ephemeral
 * browsing, matching pre-ENG-7748 behaviour). A mint never throws into the poll
 * loop.
 *
 * State is in-memory per agent. A manager restart re-mints (the previous session
 * is left to Anchor's own idle/max-duration expiry, and is also best-effort ended
 * when we mint its replacement), which is acceptable - no persistence needed.
 *
 * VERIFICATION (// anchor-verify): the max-duration / refresh-margin defaults are
 * conservative guesses; Anchor's real session caps are confirmed live in
 * ENG-7749. We compute expiry from the max-duration we REQUEST, so correctness
 * doesn't depend on the response reporting expiry.
 */

import { AnchorSessionClient, type AnchorSession, type CreateAnchorSessionInput } from '@augmented/core';

/** Requested session lifetime. We re-mint before this elapses. // anchor-verify */
export const DEFAULT_ANCHOR_SESSION_MAX_DURATION_MINUTES = 60;
/** Re-mint once the session is within this margin of its max duration. */
export const DEFAULT_ANCHOR_SESSION_REFRESH_MARGIN_MINUTES = 5;

/** Minimal surface of the Anchor client the lifecycle needs (injectable for tests). */
export interface AnchorClientLike {
  createSession(input: CreateAnchorSessionInput): Promise<AnchorSession>;
  endSession(sessionId: string): Promise<void>;
}

interface AnchorSessionRecord {
  sessionId: string;
  profileName: string;
  /** Epoch ms after which we consider the session due for a re-mint. */
  expiresAt: number;
}

export interface EnsureAnchorSessionParams {
  agentId: string;
  /** From `integration.config.profile_name`. Empty/absent ⇒ stateless (no mint). */
  profileName?: string | null;
  /** Plaintext Anchor api key from `integration.credentials.api_key`. */
  apiKey?: string | null;
  /** Current time (injected for tests). */
  now: number;
  /** Optional logger. */
  log?: (msg: string) => void;
}

export interface AnchorSessionLifecycleOptions {
  maxDurationMinutes?: number;
  refreshMarginMinutes?: number;
  /**
   * Idle timeout REQUESTED on the session. Defaults to `maxDurationMinutes` so
   * the two bounds coincide: without this, Anchor's own (possibly shorter)
   * default idle timeout could kill a session we still consider valid for the
   * full max-duration window, and the manager would keep handing the agent a
   * dead session id. By requesting idle == max we make max-duration the single
   * effective bound. (True activity-aware idle handling would need an
   * agent-browser-activity signal the manager does not have; this conservative
   * alignment is the safe floor.)
   */
  idleTimeoutMinutes?: number;
  /** Builds a client for a given api key. Overridable in tests. */
  createClient?: (apiKey: string) => AnchorClientLike;
}

export class AnchorSessionLifecycle {
  private readonly store = new Map<string, AnchorSessionRecord>();
  private readonly maxDurationMs: number;
  private readonly refreshMarginMs: number;
  private readonly idleTimeoutMinutes: number;
  private readonly createClient: (apiKey: string) => AnchorClientLike;

  constructor(opts: AnchorSessionLifecycleOptions = {}) {
    const maxDurationMinutes = opts.maxDurationMinutes ?? DEFAULT_ANCHOR_SESSION_MAX_DURATION_MINUTES;
    this.maxDurationMs = maxDurationMinutes * 60_000;
    this.refreshMarginMs = (opts.refreshMarginMinutes ?? DEFAULT_ANCHOR_SESSION_REFRESH_MARGIN_MINUTES) * 60_000;
    // Align idle with max by default so a cached session can't idle-die early.
    this.idleTimeoutMinutes = opts.idleTimeoutMinutes ?? maxDurationMinutes;
    this.createClient = opts.createClient ?? ((apiKey) => new AnchorSessionClient({ apiKey }));
  }

  /**
   * Return the session id the agent should bind, minting or re-minting as needed.
   * Returns null for the stateless case (no profile / no key / mint failure with
   * no valid fallback) - the manager then leaves `ANCHOR_BROWSER_SESSION_ID`
   * empty. Never throws.
   */
  async ensure(params: EnsureAnchorSessionParams): Promise<string | null> {
    const { agentId, now } = params;
    const log = params.log ?? (() => {});
    const profileName = params.profileName?.trim() || '';
    const apiKey = params.apiKey?.trim() || '';
    const existing = this.store.get(agentId);

    // No profile bound ⇒ stateless. Drop any session we were holding.
    if (!profileName) {
      if (existing) {
        this.endBestEffort(apiKey, existing.sessionId, log);
        this.store.delete(agentId);
      }
      return null;
    }

    // Can't mint without an api key. Keep a still-valid session if we have one.
    if (!apiKey) {
      if (existing && existing.profileName === profileName && now < existing.expiresAt) {
        return existing.sessionId;
      }
      log(`[anchor-session] ${agentId}: profile "${profileName}" but no api key - stateless`);
      return null;
    }

    // Reuse a valid session for the same profile that isn't near expiry.
    if (existing && existing.profileName === profileName && now < existing.expiresAt - this.refreshMarginMs) {
      return existing.sessionId;
    }

    // Mint (first bind, expiry-driven re-mint, or profile change).
    try {
      const client = this.createClient(apiKey);
      const session = await client.createSession({
        profileName,
        dedicatedStickyIp: true,
        maxDurationMinutes: this.maxDurationMs / 60_000,
        idleTimeoutMinutes: this.idleTimeoutMinutes,
      });
      // Expire at the sooner of the two bounds we requested (they coincide by
      // default). Guards against an idle timeout shorter than max-duration
      // leaving a dead session cached as "valid".
      const effectiveMs = Math.min(this.maxDurationMs, this.idleTimeoutMinutes * 60_000);
      this.store.set(agentId, {
        sessionId: session.sessionId,
        profileName,
        expiresAt: now + effectiveMs,
      });
      // Retire the previous session so we don't leak it.
      if (existing && existing.sessionId !== session.sessionId) {
        this.endBestEffort(apiKey, existing.sessionId, log);
      }
      log(`[anchor-session] ${agentId}: minted session for profile "${profileName}"`);
      return session.sessionId;
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      // Fail-soft: keep a still-valid session if we have one, else go stateless.
      if (existing && existing.profileName === profileName && now < existing.expiresAt) {
        log(`[anchor-session] ${agentId}: re-mint failed (${reason}) - keeping current session`);
        return existing.sessionId;
      }
      log(`[anchor-session] ${agentId}: mint failed (${reason}) - degrading to stateless`);
      return null;
    }
  }

  /** Forget an agent's session (e.g. on decommission). Best-effort ends it. */
  forget(agentId: string, apiKey?: string | null, log: (msg: string) => void = () => {}): void {
    const existing = this.store.get(agentId);
    if (!existing) return;
    this.endBestEffort(apiKey?.trim() || '', existing.sessionId, log);
    this.store.delete(agentId);
  }

  private endBestEffort(apiKey: string, sessionId: string, log: (msg: string) => void): void {
    if (!apiKey || !sessionId) return;
    // Fire-and-forget; ending a session must never block or fail the poll loop.
    // Never log the session id - it is the live `anchor-session-id` credential.
    void this.createClient(apiKey)
      .endSession(sessionId)
      .catch((err) => log(`[anchor-session] failed to end a stale session: ${(err as Error).message}`));
  }
}
